import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

type UploadCategory = 'bcpFramework' | 'bcpPlan' | 'businessImpact';

type UploadedFileItem = {
  id: string;
  name: string;
  sizeLabel: string;
};

type UploadSection = {
  key: UploadCategory;
  title: string;
  description: string;
  hint: string;
  filesLabel: string;
};

@Component({
  selector: 'app-step-two',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './step-two.component.html',
  styleUrl: './step-two.component.scss',
})
export class StepTwoComponent {
  @Input({ required: true }) uploadSections!: UploadSection[];
  @Input({ required: true }) uploadedFiles!: Record<
    UploadCategory,
    UploadedFileItem[]
  >;

  stepTwoDragOverCategory: UploadCategory | null = null;

  onStepTwoFileSelected(event: Event, category: UploadCategory): void {
    const input = event.target as HTMLInputElement;
    this.appendFiles(category, input.files);
    input.value = '';
  }

  onStepTwoDragOver(event: DragEvent, category: UploadCategory): void {
    event.preventDefault();
    this.stepTwoDragOverCategory = category;
  }

  onStepTwoDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.stepTwoDragOverCategory = null;
  }

  onStepTwoDrop(event: DragEvent, category: UploadCategory): void {
    event.preventDefault();
    this.stepTwoDragOverCategory = null;
    this.appendFiles(category, event.dataTransfer?.files ?? null);
  }

  removeUploadedFile(category: UploadCategory, fileId: string): void {
    this.uploadedFiles[category] = this.uploadedFiles[category].filter(
      (file) => file.id !== fileId,
    );
  }

  private appendFiles(category: UploadCategory, files: FileList | null): void {
    const nextItems = this.createUploadedFileItems(category, files);

    if (!nextItems.length) {
      return;
    }

    this.uploadedFiles[category] = [
      ...this.uploadedFiles[category],
      ...nextItems,
    ];
  }

  private createUploadedFileItems(
    prefix: string,
    files: FileList | null,
  ): UploadedFileItem[] {
    if (!files?.length) {
      return [];
    }

    return Array.from(files).map((file, index) => ({
      id: `${prefix}-${file.name}-${file.size}-${Date.now()}-${index}`,
      name: file.name,
      sizeLabel: this.formatFileSize(file.size),
    }));
  }

  private formatFileSize(sizeInBytes: number): string {
    if (!sizeInBytes) {
      return '0 KB';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let value = sizeInBytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    const roundedValue =
      value >= 10 || unitIndex === 0
        ? Math.round(value)
        : Number(value.toFixed(1));
    return `${roundedValue} ${units[unitIndex]}`;
  }
}
